/**
 * Mock Tree Data for Tree Grid Demos
 * Contains hierarchical data structures for testing tree functionality
 */

/**
 * Organization Employee
 * Represents an employee in an organizational hierarchy
 */
export interface OrgEmployee {
  _id: string;
  name: string;
  position: string;
  department: string;
  email: string;
  salary: number;
  hireDate: Date;
  reportsTo?: string;
  children?: OrgEmployee[];
}

/**
 * Product Category
 * Represents a product category in a hierarchical catalog
 */
export interface ProductCategory {
  _id: string;
  name: string;
  description: string;
  productCount: number;
  active: boolean;
  createdAt: Date;
  children?: ProductCategory[];
}

/**
 * Mock Organizational Chart Data
 * CEO -> C-Level -> Managers -> Team Members
 */
export const MOCK_ORG_CHART: OrgEmployee[] = [
  {
    _id: '1',
    name: 'María González',
    position: 'CEO',
    department: 'Executive',
    email: 'maria.gonzalez@company.com',
    salary: 250000,
    hireDate: new Date('2015-01-15'),
    children: [
      {
        _id: '2',
        name: 'Carlos Rodríguez',
        position: 'CTO',
        department: 'Technology',
        email: 'carlos.rodriguez@company.com',
        salary: 180000,
        hireDate: new Date('2016-03-20'),
        reportsTo: '1',
        children: [
          {
            _id: '3',
            name: 'Ana Martínez',
            position: 'Engineering Manager',
            department: 'Technology',
            email: 'ana.martinez@company.com',
            salary: 130000,
            hireDate: new Date('2017-06-10'),
            reportsTo: '2',
            children: [
              {
                _id: '4',
                name: 'Luis Pérez',
                position: 'Senior Developer',
                department: 'Technology',
                email: 'luis.perez@company.com',
                salary: 95000,
                hireDate: new Date('2018-09-05'),
                reportsTo: '3',
              },
              {
                _id: '5',
                name: 'Carmen López',
                position: 'Frontend Developer',
                department: 'Technology',
                email: 'carmen.lopez@company.com',
                salary: 85000,
                hireDate: new Date('2019-02-14'),
                reportsTo: '3',
              },
              {
                _id: '6',
                name: 'Diego Sánchez',
                position: 'Backend Developer',
                department: 'Technology',
                email: 'diego.sanchez@company.com',
                salary: 88000,
                hireDate: new Date('2019-05-20'),
                reportsTo: '3',
              },
            ],
          },
          {
            _id: '7',
            name: 'Patricia Fernández',
            position: 'DevOps Manager',
            department: 'Technology',
            email: 'patricia.fernandez@company.com',
            salary: 125000,
            hireDate: new Date('2017-08-15'),
            reportsTo: '2',
            children: [
              {
                _id: '8',
                name: 'Roberto García',
                position: 'DevOps Engineer',
                department: 'Technology',
                email: 'roberto.garcia@company.com',
                salary: 90000,
                hireDate: new Date('2018-11-10'),
                reportsTo: '7',
              },
              {
                _id: '9',
                name: 'Isabel Ruiz',
                position: 'Cloud Architect',
                department: 'Technology',
                email: 'isabel.ruiz@company.com',
                salary: 105000,
                hireDate: new Date('2019-01-08'),
                reportsTo: '7',
              },
            ],
          },
        ],
      },
      {
        _id: '10',
        name: 'Juan Herrera',
        position: 'CFO',
        department: 'Finance',
        email: 'juan.herrera@company.com',
        salary: 175000,
        hireDate: new Date('2016-05-12'),
        reportsTo: '1',
        children: [
          {
            _id: '11',
            name: 'Laura Moreno',
            position: 'Accounting Manager',
            department: 'Finance',
            email: 'laura.moreno@company.com',
            salary: 110000,
            hireDate: new Date('2017-09-20'),
            reportsTo: '10',
            children: [
              {
                _id: '12',
                name: 'Miguel Torres',
                position: 'Senior Accountant',
                department: 'Finance',
                email: 'miguel.torres@company.com',
                salary: 75000,
                hireDate: new Date('2018-03-15'),
                reportsTo: '11',
              },
              {
                _id: '13',
                name: 'Sofía Ramírez',
                position: 'Junior Accountant',
                department: 'Finance',
                email: 'sofia.ramirez@company.com',
                salary: 55000,
                hireDate: new Date('2020-06-01'),
                reportsTo: '11',
              },
            ],
          },
          {
            _id: '14',
            name: 'Pablo Castro',
            position: 'Financial Analyst',
            department: 'Finance',
            email: 'pablo.castro@company.com',
            salary: 80000,
            hireDate: new Date('2019-04-10'),
            reportsTo: '10',
          },
        ],
      },
      {
        _id: '15',
        name: 'Elena Vargas',
        position: 'CMO',
        department: 'Marketing',
        email: 'elena.vargas@company.com',
        salary: 170000,
        hireDate: new Date('2016-07-18'),
        reportsTo: '1',
        children: [
          {
            _id: '16',
            name: 'Andrés Ortiz',
            position: 'Marketing Manager',
            department: 'Marketing',
            email: 'andres.ortiz@company.com',
            salary: 95000,
            hireDate: new Date('2018-01-22'),
            reportsTo: '15',
            children: [
              {
                _id: '17',
                name: 'Valentina Delgado',
                position: 'Social Media Specialist',
                department: 'Marketing',
                email: 'valentina.delgado@company.com',
                salary: 60000,
                hireDate: new Date('2020-03-05'),
                reportsTo: '16',
              },
              {
                _id: '18',
                name: 'Javier Méndez',
                position: 'Content Marketing',
                department: 'Marketing',
                email: 'javier.mendez@company.com',
                salary: 65000,
                hireDate: new Date('2020-08-12'),
                reportsTo: '16',
              },
            ],
          },
        ],
      },
      {
        _id: '19',
        name: 'Cristina Silva',
        position: 'CHRO',
        department: 'Human Resources',
        email: 'cristina.silva@company.com',
        salary: 160000,
        hireDate: new Date('2016-09-25'),
        reportsTo: '1',
        children: [
          {
            _id: '20',
            name: 'Fernando Ramos',
            position: 'HR Manager',
            department: 'Human Resources',
            email: 'fernando.ramos@company.com',
            salary: 90000,
            hireDate: new Date('2018-05-15'),
            reportsTo: '19',
            children: [
              {
                _id: '21',
                name: 'Beatriz Navarro',
                position: 'Recruiter',
                department: 'Human Resources',
                email: 'beatriz.navarro@company.com',
                salary: 58000,
                hireDate: new Date('2019-11-20'),
                reportsTo: '20',
              },
            ],
          },
        ],
      },
    ],
  },
];

/**
 * Mock Product Categories Data
 * Electronics -> Computers -> Laptops, Desktops, etc.
 */
export const MOCK_PRODUCT_CATEGORIES: ProductCategory[] = [
  {
    _id: 'cat-1',
    name: 'Electrónica',
    description: 'Dispositivos y productos electrónicos',
    productCount: 1250,
    active: true,
    createdAt: new Date('2020-01-10'),
    children: [
      {
        _id: 'cat-2',
        name: 'Computadoras',
        description: 'Computadoras y accesorios',
        productCount: 450,
        active: true,
        createdAt: new Date('2020-01-15'),
        children: [
          {
            _id: 'cat-3',
            name: 'Laptops',
            description: 'Computadoras portátiles',
            productCount: 180,
            active: true,
            createdAt: new Date('2020-02-01'),
          },
          {
            _id: 'cat-4',
            name: 'Desktops',
            description: 'Computadoras de escritorio',
            productCount: 120,
            active: true,
            createdAt: new Date('2020-02-01'),
          },
          {
            _id: 'cat-5',
            name: 'Monitores',
            description: 'Pantallas y monitores',
            productCount: 95,
            active: true,
            createdAt: new Date('2020-02-05'),
          },
          {
            _id: 'cat-6',
            name: 'Accesorios',
            description: 'Teclados, ratones, etc.',
            productCount: 55,
            active: true,
            createdAt: new Date('2020-02-10'),
          },
        ],
      },
      {
        _id: 'cat-7',
        name: 'Teléfonos',
        description: 'Smartphones y teléfonos',
        productCount: 320,
        active: true,
        createdAt: new Date('2020-01-20'),
        children: [
          {
            _id: 'cat-8',
            name: 'Smartphones',
            description: 'Teléfonos inteligentes',
            productCount: 250,
            active: true,
            createdAt: new Date('2020-02-15'),
          },
          {
            _id: 'cat-9',
            name: 'Feature Phones',
            description: 'Teléfonos básicos',
            productCount: 70,
            active: false,
            createdAt: new Date('2020-02-15'),
          },
        ],
      },
      {
        _id: 'cat-10',
        name: 'Tablets',
        description: 'Tabletas y e-readers',
        productCount: 180,
        active: true,
        createdAt: new Date('2020-01-25'),
      },
      {
        _id: 'cat-11',
        name: 'Audio',
        description: 'Audífonos y bocinas',
        productCount: 300,
        active: true,
        createdAt: new Date('2020-01-30'),
        children: [
          {
            _id: 'cat-12',
            name: 'Audífonos',
            description: 'Auriculares y audífonos',
            productCount: 180,
            active: true,
            createdAt: new Date('2020-03-01'),
          },
          {
            _id: 'cat-13',
            name: 'Bocinas',
            description: 'Altavoces y bocinas',
            productCount: 120,
            active: true,
            createdAt: new Date('2020-03-01'),
          },
        ],
      },
    ],
  },
  {
    _id: 'cat-14',
    name: 'Hogar y Jardín',
    description: 'Productos para el hogar',
    productCount: 850,
    active: true,
    createdAt: new Date('2020-02-01'),
    children: [
      {
        _id: 'cat-15',
        name: 'Muebles',
        description: 'Muebles para el hogar',
        productCount: 420,
        active: true,
        createdAt: new Date('2020-02-10'),
        children: [
          {
            _id: 'cat-16',
            name: 'Sala',
            description: 'Muebles de sala',
            productCount: 150,
            active: true,
            createdAt: new Date('2020-03-05'),
          },
          {
            _id: 'cat-17',
            name: 'Comedor',
            description: 'Muebles de comedor',
            productCount: 120,
            active: true,
            createdAt: new Date('2020-03-05'),
          },
          {
            _id: 'cat-18',
            name: 'Recámara',
            description: 'Muebles de recámara',
            productCount: 150,
            active: true,
            createdAt: new Date('2020-03-05'),
          },
        ],
      },
      {
        _id: 'cat-19',
        name: 'Jardín',
        description: 'Productos para jardín',
        productCount: 230,
        active: true,
        createdAt: new Date('2020-02-15'),
        children: [
          {
            _id: 'cat-20',
            name: 'Herramientas',
            description: 'Herramientas de jardín',
            productCount: 95,
            active: true,
            createdAt: new Date('2020-03-10'),
          },
          {
            _id: 'cat-21',
            name: 'Plantas',
            description: 'Plantas y semillas',
            productCount: 135,
            active: true,
            createdAt: new Date('2020-03-10'),
          },
        ],
      },
      {
        _id: 'cat-22',
        name: 'Decoración',
        description: 'Artículos decorativos',
        productCount: 200,
        active: true,
        createdAt: new Date('2020-02-20'),
      },
    ],
  },
  {
    _id: 'cat-23',
    name: 'Ropa y Accesorios',
    description: 'Moda y accesorios',
    productCount: 2100,
    active: true,
    createdAt: new Date('2020-02-05'),
    children: [
      {
        _id: 'cat-24',
        name: 'Ropa Hombre',
        description: 'Ropa para caballero',
        productCount: 850,
        active: true,
        createdAt: new Date('2020-03-01'),
      },
      {
        _id: 'cat-25',
        name: 'Ropa Mujer',
        description: 'Ropa para dama',
        productCount: 950,
        active: true,
        createdAt: new Date('2020-03-01'),
      },
      {
        _id: 'cat-26',
        name: 'Calzado',
        description: 'Zapatos y calzado',
        productCount: 300,
        active: true,
        createdAt: new Date('2020-03-05'),
        children: [
          {
            _id: 'cat-27',
            name: 'Deportivo',
            description: 'Calzado deportivo',
            productCount: 150,
            active: true,
            createdAt: new Date('2020-04-01'),
          },
          {
            _id: 'cat-28',
            name: 'Formal',
            description: 'Calzado formal',
            productCount: 150,
            active: true,
            createdAt: new Date('2020-04-01'),
          },
        ],
      },
    ],
  },
];
